import { useMemo, type ReactNode } from 'react';
import { Empty } from '@viquard/doc-ui/components';
import type {
  CalendarEvent,
  CalendarEventLink,
  CalendarGroup,
  CalendarKindMap,
} from '../types.js';
import {
  addDays,
  formatDayLong,
  formatHourLabel,
  instantFromZonedWallClock,
  isDateOnlyISO,
  isoDate,
  localDateKey,
  localTime,
  parseISODate,
  todayPlain,
} from '../utils/dateUtils.js';
import { CalendarHeader } from '../components/CalendarHeader.js';
import { RailCard } from '../components/RailCard.js';
import { EventCard } from '../components/EventCard.js';
import { DayStrip } from '../components/DayStrip.js';
import { NowIndicator } from '../components/NowIndicator.js';
import { AllDayStrip } from '../components/AllDayStrip.js';
import { EventTimeBlock } from '../components/EventTimeBlock.js';
import { layoutDayEvents } from '../utils/layout.js';
import { applyEventDragCommit } from '../utils/dragApply.js';
import { useSwipe } from '../hooks/useSwipe.js';
import { useGridDragCreate } from '../hooks/useGridDragCreate.js';
import type { EventMenuActions } from '../utils/eventMenu.js';
import { eventInstanceKey } from '../utils/identity.js';
import { formatMessage, useCalendarMessages } from '../i18n.js';

export interface DayViewProps {
  readonly selectedDate: string;
  readonly onSelectedDateChange: (next: string) => void;
  readonly today: string;
  readonly events: readonly CalendarEvent[];
  readonly kindTheme: CalendarKindMap;
  readonly groups?: readonly CalendarGroup[] | undefined;
  readonly locale?: string | undefined;
  readonly weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | undefined;
  readonly viewerTz?: string | undefined;
  readonly onSelectEvent?: ((event: CalendarEvent) => void) | undefined;
  readonly onSelectLink?:
    | ((link: CalendarEventLink, event: CalendarEvent) => void)
    | undefined;
  readonly onUpdateEvent?:
    | ((id: string, patch: { readonly startsAt: string; readonly endsAt: string | null }) => void)
    | undefined;
  readonly canUpdateEvent?: ((event: CalendarEvent) => boolean) | undefined;
  readonly onCreateEvent?:
    | ((draft: { readonly startsAt: string; readonly endsAt?: string }) => void)
    | undefined;
  readonly contextMenu?: ((event: CalendarEvent) => EventMenuActions) | undefined;
  readonly startHour?: number | undefined;
  readonly endHour?: number | undefined;
  /** When true, render the mobile-shaped variant: DayStrip header for
   *  quick day jumps, swipe-to-prev/next, no side rail. */
  readonly mobile?: boolean | undefined;
  readonly renderDayRailExtras?: ((dateKey: string) => ReactNode) | undefined;
  readonly renderEventExtras?: ((event: CalendarEvent) => ReactNode) | undefined;
  readonly selectedEventKey?: string | null | undefined;
}

const HOUR_HEIGHT = 60;

export function DayView(props: DayViewProps): ReactNode {
  const messages = useCalendarMessages();
  const {
    selectedDate,
    onSelectedDateChange,
    today,
    events,
    kindTheme,
    groups,
    locale,
    weekStartsOn = 0,
    viewerTz,
    onSelectEvent,
    onSelectLink,
    onUpdateEvent,
    canUpdateEvent,
    onCreateEvent,
    contextMenu,
    startHour = 6,
    endHour = 22,
    mobile = false,
    renderDayRailExtras,
    renderEventExtras,
    selectedEventKey,
  } = props;

  const date = useMemo(() => parseISODate(selectedDate), [selectedDate]);
  const isToday = selectedDate === today;
  const hours = useMemo(() => {
    const list: number[] = [];
    for (let h = startHour; h <= endHour; h++) list.push(h);
    return list;
  }, [startHour, endHour]);

  const { allDay, timed } = useMemo(() => {
    const all: CalendarEvent[] = [];
    const tm: CalendarEvent[] = [];
    for (const e of events) {
      if (localDateKey(e.startsAt, viewerTz) !== selectedDate) continue;
      if (e.allDay === true || isDateOnlyISO(e.startsAt)) all.push(e);
      else tm.push(e);
    }
    return { allDay: all, timed: tm };
  }, [events, selectedDate, viewerTz]);

  const positioned = useMemo(
    () => layoutDayEvents(timed, viewerTz),
    [timed, viewerTz],
  );

  const goPrev = (): void => { onSelectedDateChange(isoDate(addDays(date, -1))); };
  const goNext = (): void => { onSelectedDateChange(isoDate(addDays(date, 1))); };
  // Don't run swipe when the gesture started inside the DayStrip pill row.
  const swipe = useSwipe(
    mobile
      ? { onLeft: goNext, onRight: goPrev, ignoreSelector: '.vq-cal__day-strip' }
      : {},
  );

  // Counts per day in this week, used by the mobile DayStrip.
  const eventCounts = useMemo(() => {
    if (!mobile) return undefined;
    const map = new Map<string, number>();
    events.forEach((e) => {
      const key = localDateKey(e.startsAt, viewerTz);
      map.set(key, (map.get(key) ?? 0) + 1);
    });
    return map;
  }, [events, mobile, viewerTz]);

  // Drag-to-create on the day track.
  const dragCreate = useGridDragCreate({
    hourHeight: HOUR_HEIGHT,
    startHour,
    snapMinutes: 30,
    disabled: onCreateEvent === undefined,
    onCommit: (c) => {
      const startMs = hourFractionToInstant(c.dayKey, c.startHourFraction, viewerTz);
      const endMs = hourFractionToInstant(c.dayKey, c.endHourFraction, viewerTz);
      onCreateEvent?.({
        startsAt: new Date(startMs).toISOString(),
        endsAt: new Date(endMs).toISOString(),
      });
    },
  });

  const ghostStyle =
    dragCreate.ghost !== null && dragCreate.ghost.dayKey === selectedDate
      ? {
          top: dragCreate.ghost.top,
          height: dragCreate.ghost.height,
          left: 12,
          right: 16,
        }
      : null;

  return (
    <div
      className={mobile ? 'vq-cal__split--mobile' : 'vq-cal__split vq-cal__split--day'}
      {...(mobile ? swipe : {})}
    >
      <div className="vq-cal__panel">
        <CalendarHeader
          label={formatDayLong(date, locale)}
          onPrev={goPrev}
          onNext={goNext}
          onToday={() => { onSelectedDateChange(isoDate(todayPlain(viewerTz))); }}
        />
        {mobile && (
          <DayStrip
            anchor={selectedDate}
            selected={selectedDate}
            today={today}
            onSelect={onSelectedDateChange}
            weekStartsOn={weekStartsOn}
            locale={locale}
            eventCounts={eventCounts}
          />
        )}
        {allDay.length > 0 && (
          <AllDayStrip
            events={allDay}
            kindTheme={kindTheme}
            onSelectEvent={onSelectEvent}
            selectedEventKey={selectedEventKey}
          />
        )}
        <div
          className="vq-cal__day-grid"
          style={{ ['--vq-cal-hour-h' as string]: `${HOUR_HEIGHT}px` }}
        >
          <div className="vq-cal__day-gutter">
            {hours.map((h) => (
              <div key={h} className="vq-cal__day-hour-label">
                {formatHourLabel(h, locale)}
              </div>
            ))}
          </div>
          <div
            className="vq-cal__day-track"
            data-day-key={selectedDate}
            onPointerDown={dragCreate.bind.onPointerDown}
          >
            {hours.map((h) => (
              <div key={h} className="vq-cal__day-hour" />
            ))}
            <NowIndicator
              startHour={startHour}
              endHour={endHour}
              hourHeight={HOUR_HEIGHT}
              active={isToday}
              locale={locale}
              viewerTz={viewerTz}
            />
            {timed.length === 0 && dragCreate.ghost === null && (
              <div className="vq-cal__day-empty">
                <Empty
                  size="compact"
                  title={messages.quietDay}
                  description={
                    onCreateEvent !== undefined
                      ? messages.dayDragHint
                      : messages.dayNoScheduled
                  }
                />
              </div>
            )}
            {positioned.map((p) => (
              <EventTimeBlock
                key={eventInstanceKey(p.event)}
                positioned={p}
                kindTheme={kindTheme}
                startHour={startHour}
                hourHeight={HOUR_HEIGHT}
                timeLabel={localTime(p.event.startsAt, viewerTz)}
                showKindLabel
                density="comfortable"
                onClick={onSelectEvent}
                dayKey={selectedDate}
                onMoveCommit={
                  onUpdateEvent !== undefined && canUpdateEvent?.(p.event) !== false
                    ? (commit) => {
                        const patch = applyEventDragCommit(p.event, commit, selectedDate, {
                          viewerTz,
                        });
                        if (patch !== null) onUpdateEvent(p.event.id, patch);
                      }
                    : undefined
                }
                onResizeCommit={
                  onUpdateEvent !== undefined && canUpdateEvent?.(p.event) !== false
                    ? (commit) => {
                        const patch = applyEventDragCommit(p.event, commit, selectedDate, {
                          viewerTz,
                        });
                        if (patch !== null) onUpdateEvent(p.event.id, patch);
                      }
                    : undefined
                }
                selected={selectedEventKey === eventInstanceKey(p.event)}
                contextMenu={contextMenu?.(p.event)}
              />
            ))}
            {ghostStyle !== null && (
              <div className="vq-cal__day-ghost" style={ghostStyle} aria-hidden="true">
                <span>{messages.newEvent}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {!mobile && (
        <aside className="vq-cal__rail" aria-label={messages.daySummaryAriaLabel}>
          <RailCard eyebrow={messages.railEyebrow} title={formatDayLong(date, locale)}>
            <p className="vq-cal__rail-sub">
              {(() => {
                const total = timed.length + allDay.length;
                const tpl = total === 1 ? messages.dayEventsScheduledOne : messages.dayEventsScheduledOther;
                return formatMessage(tpl, { count: total });
              })()}
            </p>
          </RailCard>
          {(timed.length > 0 || allDay.length > 0) && (
            <RailCard eyebrow={messages.atAGlance}>
              <div className="vq-cal__card-stack">
                {[...allDay, ...timed].map((e) => (
                  <EventCard
                    key={eventInstanceKey(e)}
                    event={e}
                    kindTheme={kindTheme}
                    groups={groups}
                    onClick={onSelectEvent}
                    onSelectLink={onSelectLink}
                    viewerTz={viewerTz}
                    extras={renderEventExtras?.(e)}
                    selected={selectedEventKey === eventInstanceKey(e)}
                    contextMenu={contextMenu?.(e)}
                    density="compact"
                  />
                ))}
              </div>
            </RailCard>
          )}
          {renderDayRailExtras?.(selectedDate)}
        </aside>
      )}
      {/* Mobile: the side rail collapses entirely, but consumer-
       *  injected day-rail extras (e.g. AvailabilityForDate booking
       *  cards) are domain content — they have to surface somewhere.
       *  Render them as a stacked section below the day grid so the
       *  feature parity matches desktop. The 'at-a-glance' card list
       *  is intentionally omitted on mobile because the day timeline
       *  above already shows everything. */}
      {mobile && renderDayRailExtras?.(selectedDate)}
    </div>
  );
}

function hourFractionToInstant(
  dayKey: string,
  fraction: number,
  viewerTz?: string,
): number {
  const d = parseISODate(dayKey);
  const wholeHour = Math.floor(fraction);
  const minute = Math.round((fraction - wholeHour) * 60);
  return instantFromZonedWallClock(d.year, d.month, d.day, wholeHour, minute, viewerTz);
}
